import { useState, useMemo } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Container,
  Box,
  Typography,
  TextField,
  Chip,
  Checkbox,
  Collapse,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Paper,
  Stack,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { useAuth } from "../../../../../auth";
import {
  getDeleteGroupMutationOptions,
  getDisableGroupMutationOptions,
  getFindGroupQueryOptions,
  getListGroupPermissionsQueryOptions,
  getListGroupMembersQueryOptions,
  getListPermissionQueryOptions,
  getEnableGroupMutationOptions,
} from "../../../../../api/bpm/endpoints";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ConfirmModal } from "../../../../../components/ConfirmModal";
import type { SmartPermissionDtoApi, SmartGroupWitlAllPropertiesDto } from "../../../../../api/bpm/model";
import theme from "../../../../../theme";
import { useTranslation } from "react-i18next";
import { deduplicatePermissions } from "../../../../../utils/permissionTreeMapper";

export const Route = createFileRoute("/__auth/admin/groups/$groupId/")({
  component: GroupViewPage,
  staticData: {
    title: "routes.viewGroup",
  },
});

interface PermissionLeaf {
  id: number;
  label: string;
  checked: boolean;
}

interface ModuleNode {
  name: string;
  permissions: PermissionLeaf[];
  allChecked: boolean;
  someChecked: boolean;
}

function buildPermissionTree(
  allPermissions: SmartPermissionDtoApi[],
  groupPermissionIds: number[],
): ModuleNode[] {
  const moduleMap = new Map<string, PermissionLeaf[]>();
  const collator = new Intl.Collator("pt-BR", {
    sensitivity: "base",
    numeric: true,
  });

  for (const p of deduplicatePermissions(allPermissions, groupPermissionIds)) {
    if (p.id == null) continue;

    const moduleName = p.module || "Outros";

    if (!moduleMap.has(moduleName)) {
      moduleMap.set(moduleName, []);
    }
    const isChecked = groupPermissionIds.includes(p.id);
    moduleMap.get(moduleName)!.push({
      id: p.id,
      label: p.functionality || p.permission || `Permissão ${p.id}`,
      checked: isChecked,
    });
  }

  const tree: ModuleNode[] = [];
  const orderedModules = Array.from(moduleMap.keys()).sort((a, b) => {
    if (a === "Outros") return 1;
    if (b === "Outros") return -1;
    return collator.compare(a, b);
  });

  for (const moduleName of orderedModules) {
    const perms = moduleMap.get(moduleName)!;
    const orderedPermissions = [...perms].sort(
      (a, b) => collator.compare(a.label, b.label) || a.id - b.id,
    );
    const checkedCount = perms.filter((p) => p.checked).length;
    tree.push({
      name: moduleName,
      permissions: orderedPermissions,
      allChecked: perms.length > 0 && checkedCount === perms.length,
      someChecked: checkedCount > 0 && checkedCount < perms.length,
    });
  }

  return tree;
}

function PermissionTreeModule({ module }: { module: ModuleNode }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          py: 0.5,
          cursor: "pointer",
          "&:hover": { bgcolor: "action.hover", borderRadius: 1 },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <IconButton size="small" sx={{ mr: 0.5 }}>
          {expanded ? (
            <ExpandMoreIcon fontSize="small" />
          ) : (
            <ChevronRightIcon fontSize="small" />
          )}
        </IconButton>
        <Checkbox
          checked={module.allChecked}
          indeterminate={module.someChecked}
          disabled
          size="small"
          sx={{ p: 0.5 }}
        />
        <Typography variant="body2" sx={{ ml: 1 }}>
          {module.name}
        </Typography>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ pl: 6 }}>
          {module.permissions.map((perm) => (
            <Box
              key={perm.id}
              sx={{
                display: "flex",
                alignItems: "center",
                py: 0.25,
              }}
            >
              <Checkbox
                checked={perm.checked}
                disabled
                size="small"
                sx={{
                  p: 0.5,
                  "&.Mui-checked": { color: "primary.main" },
                }}
              />
              <Typography variant="body2" sx={{ ml: 1 }}>
                {perm.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

function GroupViewPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { groupId } = Route.useParams();
  const tenant = user?.tenant || "default-domain";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const {
    data: groupData,
    isLoading: isLoadingGroup,
    error: errorGroup,
  } = useQuery({
    ...getFindGroupQueryOptions(tenant, groupId),
  });
  const group = ((groupData as any)?.data || groupData) as SmartGroupWitlAllPropertiesDto | undefined;

  const { data: permissionsData, isLoading: isLoadingPermissions } =
    useQuery({
      ...getListGroupPermissionsQueryOptions(tenant, groupId),
    });
  const permissions = ((permissionsData as any)?.data || permissionsData) as SmartPermissionDtoApi[] | undefined;

  const { data: membersData, isLoading: isLoadingMembers } =
    useQuery({
      ...getListGroupMembersQueryOptions(tenant, groupId),
    });
  const members = ((membersData as any)?.data || membersData) as any[] | undefined;

  const { data: allPermissionsData, isLoading: isLoadingAllPermissions } =
    useQuery({
      ...getListPermissionQueryOptions({
        isPageable: false,
        locale: "pt",
      } as any),
    });

  const isLoading =
    isLoadingGroup ||
    isLoadingPermissions ||
    isLoadingMembers ||
    isLoadingAllPermissions;

  const permissionTree = useMemo(() => {
    let allPerms: SmartPermissionDtoApi[] = [];
    const responseData = (allPermissionsData as any)?.data || allPermissionsData;
    if (Array.isArray(responseData)) {
      allPerms = responseData;
    } else if (responseData && typeof responseData === "object") {
      const page = responseData as any;
      if (Array.isArray(page.content)) {
        allPerms = page.content;
      }
    }

    if (allPerms.length === 0) {
      allPerms = permissions ?? [];
    }

    const groupPermIds = (group?.permissions ?? [])
      .map((p) => p.id)
      .filter((id): id is number => id !== undefined);
    return buildPermissionTree(allPerms, groupPermIds);
  }, [allPermissionsData, permissions]);

  const allModulesChecked =
    permissionTree.length > 0 && permissionTree.every((m) => m.allChecked);
  const someModulesChecked =
    permissionTree.some((m) => m.allChecked || m.someChecked) &&
    !allModulesChecked;

  const enableMutation = useMutation({
    ...getEnableGroupMutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getGroup", { groupId, tenant }],
      });
      queryClient.invalidateQueries({
        queryKey: ["listGroup"],
      });
      setIsConfirmOpen(false);
    },
  });

  const disableMutation = useMutation({
    ...getDisableGroupMutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getGroup", { groupId, tenant }],
      });
      queryClient.invalidateQueries({
        queryKey: ["listGroup"],
      });
      setIsConfirmOpen(false);
    },
  });

  const deleteMutation = useMutation({
    ...getDeleteGroupMutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["listGroup"],
      });
      navigate({ to: "/admin/groups", search: { page: 1, size: 10 } });
    },
  });

  const handleToggleStatus = () => {
    if (group?.enabled) {
      disableMutation.mutate({
        groupId,
        tenant,
      });
    } else {
      enableMutation.mutate({
        groupId,
        tenant,
      });
    }
  };

  const handleDeleteGroup = () => {
    deleteMutation.mutate({
      groupId,
      tenant,
    });
  };

  const isMutationLoading =
    enableMutation.isPending ||
    disableMutation.isPending ||
    deleteMutation.isPending;

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (errorGroup) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {t("admin.groups.loadError")}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() =>
            navigate({ to: "/admin/groups", search: { page: 1, size: 10 } })
          }
        >{t("admin.common.backToList")}</Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
        <Box
          sx={{
            width: 4,
            height: 40,
            bgcolor: "primary.main",
            borderRadius: 1,
            mr: 2,
          }}
        />
        <Typography variant="h5" fontWeight={600}>
          {t("admin.groups.viewData")} {group?.name}
        </Typography>
      </Box>

      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
        Dados do Grupo
      </Typography>

      <Stack spacing={2.5} sx={{ mb: 4 }}>
        <TextField
          label="Id"
          value={group?.id || ""}
          fullWidth
          size="small"
          slotProps={{ input: { readOnly: true } }}
        />
        <TextField
          label={t("common.name")}
          value={group?.name || ""}
          fullWidth
          size="small"
          slotProps={{ input: { readOnly: true } }}
        />
        <TextField
          label={t("admin.common.description")}
          value={group?.description || ""}
          fullWidth
          size="small"
          slotProps={{ input: { readOnly: true } }}
        />
      </Stack>

      <Divider sx={{ mb: 3 }} />

      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
        Permissões de Acesso
      </Typography>

      <Stack spacing={2.5} sx={{ mb: 4 }}>
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 0.5, display: "block" }}
          >
            Perfis de Acesso
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              minHeight: 42,
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {group?.accessProfiles && group.accessProfiles.length > 0 ? (
              group.accessProfiles.map((ap) => (
                <Chip
                  key={ap.id}
                  label={ap.name}
                  size="small"
                  variant="outlined"
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                Nenhum perfil de acesso associado
              </Typography>
            )}
          </Paper>
        </Box>
      </Stack>

      <Divider sx={{ mb: 3 }} />

      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        Módulos e Funcionalidades
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Checkbox
            checked={allModulesChecked}
            indeterminate={someModulesChecked}
            disabled
            size="small"
            sx={{ p: 0.5 }}
          />
          <Typography variant="body2" sx={{ ml: 1, fontWeight: 500 }}>
            Todos os módulos
          </Typography>
        </Box>

        <Divider sx={{ mb: 1 }} />

        {permissionTree.length > 0 ? (
          <Box sx={{ pl: 2 }}>
            {permissionTree.map((module) => (
              <PermissionTreeModule key={module.name} module={module} />
            ))}
          </Box>
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ py: 2, textAlign: "center" }}
          >
            Nenhuma permissão cadastrada
          </Typography>
        )}
      </Paper>

      <Divider sx={{ mb: 3 }} />

      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
        Usuários do Grupo
      </Typography>

      <Paper
        variant="outlined"
        sx={{ p: 2, mb: 4, display: "flex", flexWrap: "wrap", gap: 1 }}
      >
        {members && members.length > 0 ? (
          members.map((member) => {
            const groupTenant = group?.tenant || tenant;
            const memberTenant = (member as any).tenant;
            const isSameTenant = !groupTenant || !memberTenant || memberTenant === groupTenant;

            const chipElement = (
              <Chip
                key={member.id}
                label={member.name}
                icon={<PersonIcon />}
                size="small"
                variant="outlined"
                color={isSameTenant ? "default" : "secondary"}
                sx={
                  !isSameTenant
                    ? {
                        borderColor: "warning.main",
                        color: "warning.dark",
                        bgcolor: "action.hover",
                      }
                    : undefined
                }
              />
            );

            if (!isSameTenant && memberTenant) {
              return (
                <Tooltip
                  key={member.id}
                  title={t("admin.groups.userBelongsToOrganization", { tenant: memberTenant })}
                  arrow
                >
                  <span>{chipElement}</span>
                </Tooltip>
              );
            }

            return chipElement;
          })
        ) : (
          <Typography variant="body2" color="text.secondary">
            Nenhum usuário associado ao grupo
          </Typography>
        )}
      </Paper>

      <Divider sx={{ mb: 3 }} />

      <Box
        sx={
          isMobile
            ? { display: "flex", flexDirection: "column", gap: 1.5 }
            : { display: "flex", justifyContent: "flex-end", gap: 1.5 }
        }
      >
        <Button
          variant="contained"
          color={group?.enabled ? "error" : "success"}
          startIcon={group?.enabled ? <BlockIcon /> : <CheckCircleIcon />}
          onClick={() => setIsConfirmOpen(true)}
          disabled={isMutationLoading}
        >
          {group?.enabled ? t("common.deactivate").toUpperCase() : t("common.activate").toUpperCase()}
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => setIsDeleteConfirmOpen(true)}
          disabled={isMutationLoading}
        >{t("common.delete").toUpperCase()}</Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<EditIcon />}
          onClick={() => navigate({ to: `/admin/groups/${groupId}/edit` })}
        >{t("common.edit").toUpperCase()}</Button>
        <Button
          variant="outlined"
          onClick={() =>
            navigate({ to: "/admin/groups", search: { page: 1, size: 10 } })
          }
        >{t("common.cancel").toUpperCase()}</Button>
      </Box>
      <ConfirmModal
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleToggleStatus}
        title={group?.enabled ? t("admin.groups.deactivateTitle") : t("admin.groups.activateTitle")}
        description={
          group?.enabled
            ? t("admin.groups.deactivateConfirmation", { name: group?.name })
            : t("admin.groups.activateConfirmation", { name: group?.name })
        }
        confirmText={group?.enabled ? t("common.deactivate") : t("common.activate")}
        confirmColor={group?.enabled ? "error" : "primary"}
        isLoading={isMutationLoading}
      />
      <ConfirmModal
        open={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleDeleteGroup}
        title={t("admin.groups.deleteTitle")}
        description={t("admin.groups.deleteConfirmation", { name: group?.name })}
        confirmText={t("common.delete")}
        confirmColor="error"
        isLoading={isMutationLoading}
      />
    </Container>
  );
}
